import React, { useState } from 'react'
import { X, Key, Eye, EyeOff, Info, Copy, Check } from 'lucide-react'

interface PasswordResetModalProps {
  admin: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
  onClose: () => void
  onReset: (password: string) => void
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({ admin, onClose, onReset }) => {
  const [resetMethod, setResetMethod] = useState<'manual' | 'auto'>('auto')
  const [manualPassword, setManualPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState('')
  const [copied, setCopied] = useState(false)
  const [sendEmail, setSendEmail] = useState(true)
  const [forceChange, setForceChange] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const generatePassword = () => {
    const length = 12
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }
    setGeneratedPassword(password)
    setCopied(false)
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPassword)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (resetMethod === 'manual') {
      if (!manualPassword) {
        newErrors.password = 'Password is required'
      } else if (manualPassword.length < 8) {
        newErrors.password = 'Password must be at least 8 characters'
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(manualPassword)) {
        newErrors.password = 'Password must contain uppercase, lowercase, and numbers'
      }

      if (manualPassword !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    } else {
      if (!generatedPassword) {
        newErrors.generated = 'Please generate a password first'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      const passwordToReset = resetMethod === 'manual' ? manualPassword : generatedPassword
      onReset(passwordToReset)
    }
  }

  // Generate initial password on mount
  React.useEffect(() => {
    generatePassword()
  }, [])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Reset Password</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Reset password for {admin.firstName} {admin.lastName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Reset Method */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Reset Method
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="resetMethod"
                  value="auto"
                  checked={resetMethod === 'auto'}
                  onChange={() => setResetMethod('auto')}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Auto-generate Password</p>
                  <p className="text-xs text-muted-foreground">System will generate a secure password</p>
                </div>
              </label>
              <label className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="resetMethod"
                  value="manual"
                  checked={resetMethod === 'manual'}
                  onChange={() => setResetMethod('manual')}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Set Password Manually</p>
                  <p className="text-xs text-muted-foreground">Enter a custom password</p>
                </div>
              </label>
            </div>
          </div>

          {/* Auto-generated Password */}
          {resetMethod === 'auto' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Generated Password
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={generatedPassword}
                    readOnly
                    className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border rounded-lg font-mono text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="p-2 bg-muted hover:bg-muted/70 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
                >
                  Regenerate
                </button>
              </div>
              {errors.generated && (
                <p className="text-xs text-red-500 mt-1">{errors.generated}</p>
              )}
            </div>
          )}

          {/* Manual Password */}
          {resetMethod === 'manual' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={manualPassword}
                    onChange={(e) => {
                      setManualPassword(e.target.value)
                      if (errors.password) setErrors({ ...errors, password: '' })
                    }}
                    className={`w-full pr-10 pl-4 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.password ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' })
                    }}
                    className={`w-full pr-10 pl-4 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.confirmPassword ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          )}

          {/* Password Requirements */}
          <div className="bg-blue-500/10 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5" />
              <div className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                <p className="font-medium">Password Requirements:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Minimum 8 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one lowercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character recommended</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 rounded border-border focus:ring-primary"
              />
              <span className="text-sm text-foreground">Send new password to admin via email</span>
            </label>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
                className="w-4 h-4 rounded border-border focus:ring-primary"
              />
              <span className="text-sm text-foreground">Require password change on next login</span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PasswordResetModal